"use client";

import { Send } from "lucide-react";
import { useState, type FormEvent } from "react";

type NewsletterProps = {
  benefits: string[];
};

export function Newsletter({ benefits }: NewsletterProps) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      setError("Bitte eine gültige E-Mail eintragen.");
      setSubmitted(false);
      return;
    }
    if (!consent) {
      setError("Bitte die Datenschutz-Hinweise bestätigen.");
      setSubmitted(false);
      return;
    }

    setSubmitted(true);
    setError(null);
    setEmail("");
    setConsent(false);
  };

  return (
    <div className="home-panel home-panel--accent rounded-3xl p-6 shadow-[0_18px_36px_rgba(30,41,59,0.14)] sm:p-8">
      <div className="grid gap-8 lg:grid-cols-[1.05fr_1fr]">
        <div>
          <p className="section-kicker">Newsletter</p>
          <h2 className="mt-3 font-heading text-2xl font-semibold text-slate-900">
            Neue Produkte und Aktionen früher erhalten
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600">
            Monatlich kuratierte Updates für legale Accessoires, Produktpflege
            und saisonale Angebote.
          </p>

          <ul className="mt-5 grid gap-2">
            {benefits.map((benefit) => (
              <li
                key={benefit}
                className="rounded-xl border border-slate-300/72 bg-white/74 px-3 py-2 text-sm text-slate-700"
              >
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <form
          className="space-y-4 rounded-2xl border border-slate-300/80 bg-white/80 p-4 sm:p-5"
          onSubmit={handleSubmit}
          noValidate={true}
        >
          <div>
            <label
              htmlFor="newsletter-email"
              className="mb-2 block text-sm font-medium text-slate-800"
            >
              E-Mail-Adresse
            </label>
            <input
              id="newsletter-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="home-focus w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500"
              placeholder="name@beispiel.de"
              required={true}
            />
          </div>

          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 bg-white accent-emerald-600"
              required={true}
            />
            Ich möchte Produktneuheiten und Angebote erhalten und akzeptiere die
            Datenschutzhinweise.
          </label>

          <button
            type="submit"
            className="home-focus inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-300 to-emerald-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:from-cyan-200 hover:to-emerald-200"
          >
            Jetzt abonnieren
            <Send className="h-4 w-4" />
          </button>

          {error ? <p className="text-sm text-rose-700">{error}</p> : null}
          {submitted ? (
            <p className="text-sm text-emerald-700">
              Danke! Deine Anmeldung wurde gespeichert. Du bekommst in Kürze die
              erste Mail.
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
